import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import type { ISSOConfig } from '@utils/types/environment';
import { getSSOConfig } from './ssoConfigService';
import { TOKEN_REFRESH_BUFFER } from '@constants/common.constants';

let keycloak: Keycloak | null = null;
let readyPromise: Promise<boolean> | null = null;

export const KeycloakService = {
    async initSilent(): Promise<boolean> {
        if (readyPromise) {
            return readyPromise;
        }

        readyPromise = (async () => {
            try {
                const ssoConfig: ISSOConfig = getSSOConfig();

                keycloak = new Keycloak({
                    url: ssoConfig.url,
                    realm: ssoConfig.realm,
                    clientId: ssoConfig.clientId,
                });

                const initOptions: KeycloakInitOptions = {
                    onLoad: ssoConfig.onLoad,
                    pkceMethod: ssoConfig.pkceMethod,
                    checkLoginIframe: false,
                    silentCheckSsoRedirectUri: `${window.location.origin}/hix/resources/spa/silent-check-sso.html`,
                };

                return await keycloak.init(initOptions);
            } catch (error) {
                console.error('Error initializing Keycloak:', error);
                readyPromise = null;
                return false;
            }
        })();

        return readyPromise;
    },

    async getTokenAsync(): Promise<string | undefined> {
        try {
            const isAuthenticated = await this.initSilent();

            if (!isAuthenticated || !keycloak) {
                return undefined;
            }

            await keycloak.updateToken(TOKEN_REFRESH_BUFFER);
            return keycloak.token;
        } catch (error) {
            console.error('Error refreshing token:', error);
            return undefined;
        }
    },

    async getAuthHeader(headerName?: string): Promise<Record<string, string>> {
        const token = await this.getTokenAsync();
        if (!token) return {};

        return headerName ? { [headerName]: token } : { Authorization: `Bearer ${token}` };
    },
};

export default KeycloakService;
